import {
  all,
  call, put, select, takeEvery,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { IRestApiResponse } from 'interfaces';
import { selectAuthUser } from 'base/auth/selectors';
import { currencyFormat } from 'utils/formats';
import { CoreSliceActions } from 'base/core/slice';
import API from './constants';

import { sessionSummaryActions } from './slice';
import {
  ISessionSummaryAction, IGenerateSessionStateAction,
  ISessionSummaryUpdateAction, ISessionDeleteAction, ISupportAction,
} from './interface';

export function* fetchSessionSummaryGenerator({ payload }: ISessionSummaryAction): any {
  try {
    const authUser = yield select(selectAuthUser);
    const response: IRestApiResponse = yield call(
      request,
      { path: `${API.GET_FETCH_SESSION_SUMMARY.path}/${payload}`, method: API.GET_FETCH_SESSION_SUMMARY.method },
      null,
      true,
    );
    if (response.statusCode === 200) {
      const preferredCurrency = authUser?.attributes?.currency ?? 'AUD';
      const fee = yield call(currencyFormat, response.data.fee, response.data.currency ?? 'AUD', preferredCurrency);
      const subscriberFee = yield call(currencyFormat, response.data.subscriberFee, response.data.subscriberCurrency ?? 'AUD', preferredCurrency);
      const subscribeObserverFee = yield call(currencyFormat, response.data.subscribeObserverFee, response.data.subscribeObserverCurrency ?? 'AUD', preferredCurrency);

      const formattedData = yield {
        ...response.data, fee, subscriberFee, subscribeObserverFee,
      };
      yield put(sessionSummaryActions.fetchSessionSummarySucceeded(formattedData));
    } else {
      yield put(sessionSummaryActions.fetchSessionSummaryFailed());
    }
  } catch (error) {
    yield put(sessionSummaryActions.fetchSessionSummaryFailed());
  }
}

export function* getSessionUrlGenerator({ payload }: IGenerateSessionStateAction) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: API.GET_SESSION_URL.path, method: API.GET_SESSION_URL.method },
      { ...payload },
      true,
      false,
    );

    if (response.statusCode === 201) {
      yield put(sessionSummaryActions.getSessionUrlSuccessed(response.data));
    } else {
      yield put(sessionSummaryActions.getSessionUrlFailed());
    }
  } catch (error) {
    yield put(sessionSummaryActions.getSessionUrlFailed());
  }
}

export function* updateSessionSummeryGenerator({ payload }: ISessionSummaryUpdateAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      API.POST_UPDATE_SESSION,
      {
        id: payload.id,
        audienceType: payload?.audienceType,
        isActive: payload?.isActive,
        invitePeople: payload?.invitePeople,
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(sessionSummaryActions.updateSessionSummerySucceeded());
      if (payload?.resolve) {
        yield payload.resolve();
      }
    } else {
      yield put(sessionSummaryActions.updateSessionSummeryFailed());
      if (payload?.reject) {
        yield payload.reject();
      }
    }
  } catch (error) {
    yield put(sessionSummaryActions.updateSessionSummeryFailed());
    if (payload?.reject) {
      yield payload.reject();
    }
  }
}

export function* deleteSessionGenerator({ payload }: ISessionDeleteAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: API.DELETE_DELETE_SESSION.path, method: API.DELETE_DELETE_SESSION.method },
      {
        id: payload.id,
        reasonForDeletion: payload.reasonForDeletion,
        linkedSessionId: payload.linkedSessionId,
      },
      true,
    );
    if (response.statusCode === 200) {
      yield put(sessionSummaryActions.deleteSessionSucceeded());
      yield call(payload.resolve);
    } else {
      yield put(sessionSummaryActions.deleteSessionFailed());
      yield call(payload.reject);
    }
  } catch (error) {
    yield put(sessionSummaryActions.deleteSessionFailed());
    yield call(payload.reject);
  }
}

export function* createSupportFeedback({ payload }: ISupportAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_CREATE_SUPPORT_FEEDBACK.path}`,
        method: API.POST_CREATE_SUPPORT_FEEDBACK.method,
      },
      { ...payload },
      true,
    );
    if (response.statusCode === 201) {
      yield put(sessionSummaryActions.createSupportFeedbackSucceeded(response.data));
      yield put(
        CoreSliceActions.displayAlert({
          type: 'success',
          message: 'Support Feedback submitted successfully!',
          btnText: 'Ok',
        }),
      );
    } else {
      yield put(sessionSummaryActions.createSupportFeedbackFailed());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'error',
          message: 'There was an error submitting the feedback.',
          btnText: 'Try submitting again',
        }),
      );
    }
  } catch (error) {
    yield put(sessionSummaryActions.createSupportFeedbackFailed());
    yield put(
      CoreSliceActions.displayAlert({
        type: 'error',
        message: 'There was an error submitting the feedback.',
        btnText: 'Try submitting again',
      }),
    );
  }
}

export function* sessionSammarySaga() {
  yield all([
    takeEvery(sessionSummaryActions.fetchSessionSummary.type, fetchSessionSummaryGenerator),
    takeEvery(sessionSummaryActions.updateSessionSummery.type, updateSessionSummeryGenerator),
    takeEvery(sessionSummaryActions.deleteSession.type, deleteSessionGenerator),
    takeEvery(sessionSummaryActions.getSessionUrl.type, getSessionUrlGenerator),
    takeEvery(sessionSummaryActions.createSupportFeedback.type, createSupportFeedback),
  ]);
}

export default sessionSammarySaga;
